
import { defineComponent } from 'vue';
import { processImage, addToYouverseGroup, identifyFace } from '@/api/youid';
import useContext from '@/composition/context';
import store from '@/store';

export default defineComponent({
  props: {
    forms: {
      type: Array,
      required: false,
      default: () => []
    },
    activeForm: {
      type: Number,
      required: false,
      default: 0
    },
    webcamMode: {
      type: String,
      required: false,
      default: 'registration',
      validator: function (value) {
        return ['registration', 'verification'].includes(value)
      }
    }
  },
  setup() {
    const { translations, contextVisitor } = useContext();
    return {
      translations,
      contextVisitor
    };
  },
  data() {
    return {
      modal: {
        isActive: false,
        isCard: true,
        onClose: this.onClose,
      },
      event: 0,
      image: '',
      error: null,
      loading: false,
      cameraError: null,
    };
  },
  created() {
    const contextEvent = store.getters.contextEvent;
    this.event = contextEvent.id;

    // Check if we're in a secure context
    if (!window.isSecureContext) {
      this.cameraError = 'Camera access requires a secure context (HTTPS)';
      console.error('Not in secure context - camera access may be restricted');
    }

    // Check if mediaDevices API is available
    if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
      this.cameraError = 'Camera access is not supported in this browser';
      console.error('mediaDevices API not available');
    }

    // Add event listener with proper this binding
    this.handleNextStepEvent = (event) => {
      localStorage.removeItem('YOUVERSE_BEAMIAN_PHOTO');

      // 1 - Get the string image content
      let image_data = event.detail.image;

      // Remove the base64 prefix from string
      image_data = image_data.replace('data:image/png;base64,', '');
      image_data = image_data.replace('data:image/jpeg;base64,', '')
      image_data = image_data.replace('data:image/jpg;base64,', '');
      // console.log(' 1 - NEXT_STEP_EVENT');

      // 2 - Process and Register in Youverse
      this.registerInYouverse(image_data);

      this.image = image_data;
      localStorage.setItem('YOUVERSE_BEAMIAN_PHOTO', image_data);
    };
    document.addEventListener('NEXT_STEP_EVENT', this.handleNextStepEvent);
  },
  unmounted() {
    // Clean up event listener
    document.removeEventListener('NEXT_STEP_EVENT', this.handleNextStepEvent);
  },
  methods: {
    close() {
      this.modal.isActive = false;
      location.reload();
    },
    copy(value) {
      navigator.clipboard.writeText(value);
      alert('Text copied to clipboard');
    },
    async handleFaceCapture(image) {
      this.loading = true;
      this.error = null;
      let email = "";

      try {
        const processResponse = await processImage(image);
        const event_id = store.getters.contextEvent.id;

        if (!processResponse?.data?.length) {
          throw new Error('No face detected in image');
        }

        const result = processResponse.data[0];
        if (!result.template) {
          throw new Error('No face template generated');
        }

        // New Registration flow
        if (this.webcamMode === 'registration') {
          email = this.forms[this.activeForm]?.email;
          if (!email) {
            throw new Error('Email is required for registration');
          }
          await addToYouverseGroup(result.template, email, event_id);
          this.$emit('next');
        }
        // Edit from my profile page flow
        else if (this.webcamMode === 'edit') {
          email = this.contextVisitor.email;
          await addToYouverseGroup(result.template, email, event_id);
          this.$emit('registration-success');
          this.$emit('next');
        }

        else {
          // Verification mode - identify face in gallery
          const identifyResponse = await identifyFace(result.template, event_id);
          if (identifyResponse?.data?.length > 0) {
            const match = identifyResponse.data[0];
            if (match.score >= 0.6) {  // Using the same threshold as backend
              // Extract email from template_id and emit it with score
              this.$emit('verification-success', {
                email: match.template_id,
                score: match.score
              });
            } else {
              throw new Error('Face verification score too low');
            }
          } else {
            throw new Error('No matching face found');
          }
        }
      } catch (err) {
        this.error = err.response?.data?.message || err.message || 'An error occurred';
      } finally {
        this.loading = false;
      }
    },
    registerInYouverse(image) {
      // Call new unified handler
      this.handleFaceCapture(image);
    }
  },
});
